import { Flex, Select, Table, type TableProps } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import type { ReactNode } from "react";
import type { ColumnsType } from "../types/global.types";
import { cn } from "../utils/global.utils";
import CustomPagination from "./CustomPagination";
import CustomTitle from "./CustomTitle";

interface CustomTableProps<T extends AnyObject> extends TableProps<T> {
  /** Current page number */
  tableTitle?: string;
  tableSubTitle?: string;
  description?: string;
  customTitle?: ReactNode;
  icon?: ReactNode;
  /** Current page number */
  page?: number;
  /** Number of items per page */
  limit?: number;
  /** Total number of records */
  total?: number;
  /** Columns configuration */
  columns: ColumnsType<T>;
  /** Optional filter or control section (top-right area) */
  filterSection?: ReactNode;
  /** Hide row selector and total rows section */
  hideRowSelect?: boolean;
  /** Hide pagination footer */
  hidePagination?: boolean;
  /** Disable alternating row colors */
  hideDifferentRowColor?: boolean;
  /** Outer container class */
  rootClassName?: string;
  /** Custom top section class */
  topSectionClassName?: string;
  /** Pagination handler */
  onPaginate?: (page: number, limit: number) => void;
  /** Limit change handler */
  onLimitChange?: (val: number) => void;
}

const CustomTable = <T extends AnyObject>({
  tableTitle,
  tableSubTitle,
  loading,
  page,
  limit,
  total,
  customTitle,
  dataSource,
  description,
  icon,
  columns,
  filterSection,
  hideRowSelect = false,
  hidePagination = false,
  hideDifferentRowColor = true,
  className,
  rootClassName,
  topSectionClassName,
  onLimitChange,
  onPaginate,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  title,
  ...rest
}: CustomTableProps<T>) => {
  const hasData = Boolean(dataSource?.length);
  const showTopSection = (!hideRowSelect && total) || filterSection;
  const showPagination = !hidePagination && hasData && total && limit;

  return (
    <div
      className={cn(
        "p-4 bg-white rounded-xl shadow-[0px_3px_16px_0px_rgba(0,0,0,0.04)]",
        rootClassName
      )}
    >
      {showTopSection && (
        <Flex
          align="center"
          justify="space-between"
          className={cn("py-4 w-full", topSectionClassName)}
        >
          <div>
            {/* {tableTitle && (
              <h3 className="text-lg text-theme-gray-2 font-semibold mb-2.5">
                {tableTitle}
              </h3>
            )} */}
            <div>
              <CustomTitle.Title
                title={tableTitle}
                description={description}
                icon={icon}
                titleClassName="text-theme-slate-800 text-lg font-medium"
              />

              {tableSubTitle && (
                <div className="justify-start text-slate-600 text-sm font-normal">
                  {tableSubTitle}
                </div>
              )}
            </div>

            {!hideRowSelect && (
              <div className="flex items-center gap-2 text-theme-gray-40 font-medium">
                <span className="text-nowrap">Total Rows</span>
                <Select
                  size="small"
                  style={{ minWidth: 75, textAlign: "center" }}
                  defaultValue={limit}
                  onChange={onLimitChange}
                  options={[10, 20, 50, 100, 1000].map((value) => ({
                    label: value.toString(),
                    value,
                  }))}
                />
                <span className="text-nowrap">of {total || 0}</span>
              </div>
            )}
          </div>

          {filterSection && (
            <div className="flex items-center gap-2">{filterSection}</div>
          )}
        </Flex>
      )}
      {customTitle}
      {/* Table Section */}
      <Table
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        rowKey={(record) => record._id || record.id || record.key || record.sl}
        className={cn("custom-table custom-scrollbar", className, {
          "different-row-color": !hideDifferentRowColor && hasData,
          "table-rounded-top": !showTopSection,
          "table-hide-border-bottom": !showPagination,
        })}
        bordered
        {...rest}
      />

      {showPagination && (
        <CustomPagination
          total={total}
          limit={limit}
          page={page}
          hidePagination={hidePagination}
          handlePagination={onPaginate}
          className="border border-slate-200 border-t-0"
        />
      )}
    </div>
  );
};

export default CustomTable;
